import {
  getCurrentJurisdiction,
  type Jurisdiction,
} from '@/features/startTask/preset_task/jurisdictionResolver';

/**
 * 튜토리얼/가이드 HTML 경로 모음.
 *
 * public/tutorial 하위가 common / my / sg 폴더로 정리되어 있어,
 * 모든 walkthrough 경로를 이 파일 한 곳에서 관리한다.
 *   - common/ : MY·SG 공통 화면
 *   - my/     : Malaysia 전용
 *   - sg/     : Singapore 전용
 */

const TUTORIAL_BASE = '/tutorial';
const COMMON = `${TUTORIAL_BASE}/common`;
const MY = `${TUTORIAL_BASE}/my`;
const SG = `${TUTORIAL_BASE}/sg`;

/** 모든 walkthrough 파일의 실제 경로(정적). */
export const TUTORIAL_PATHS = {
  // 국가별 분기
  gettingStarted: {
    MY: `${MY}/getting-started-my-walkthrough.html`,
    SG: `${SG}/getting-started-sg-walkthrough.html`,
  },
  startTask: {
    MY: `${COMMON}/start-task-walkthrough.html`,
    SG: `${SG}/start-task-sg-walkthrough.html`,
  },
  eFiling: {
    MY: `${MY}/e-filing-walkthrough.html`,
    SG: `${SG}/e-filing-sg-walkthrough.html`,
  },
  // 공통
  authorisationSettings: `${COMMON}/authorisation-settings-walkthrough.html`,
  tDocAi: `${COMMON}/t-doc-ai-walkthrough.html`,
  tDocVariablesGuide: `${COMMON}/t-doc-variables-guide.html`,
  tSign: `${COMMON}/t-sign-walkthrough.html`,
  tLink: `${COMMON}/t-link-walkthrough.html`,
  tDoc: `${COMMON}/t-doc-walkthrough.html`,
  userInvite: `${COMMON}/user-invite-walkthrough.html`,
  reminder: `${COMMON}/lettaga_reminder_walkthrough.html`,
  // MY 전용
  annualReturn: `${MY}/annual-return-my-walkthrough.html`,
  // 통합 사용자 가이드(단일 파일 + 해시 라우팅)
  userGuide: `${TUTORIAL_BASE}/lettaga-user-guide.html`,
} as const;

function resolveJurisdiction(jurisdiction?: Jurisdiction | null): Jurisdiction {
  return jurisdiction || getCurrentJurisdiction() || 'MY';
}

// 언어 동기화 유틸 — 순환 의존 방지를 위해 별도 파일에서 정의하고 재노출
export { toTutorialLang, syncTutorialLanguage } from './tutorialLang';

/** Start Task (Preset) walkthrough — 국가별. */
export function getStartTaskWalkthroughUrl(jurisdiction?: Jurisdiction | null): string {
  return resolveJurisdiction(jurisdiction) === 'SG'
    ? TUTORIAL_PATHS.startTask.SG
    : TUTORIAL_PATHS.startTask.MY;
}

/** e-Filing walkthrough — 국가별. */
export function getEFilingWalkthroughUrl(jurisdiction?: Jurisdiction | null): string {
  return resolveJurisdiction(jurisdiction) === 'SG'
    ? TUTORIAL_PATHS.eFiling.SG
    : TUTORIAL_PATHS.eFiling.MY;
}

/** Getting Started(최초 온보딩) walkthrough — 국가별. */
export function getGettingStartedUrl(jurisdiction?: Jurisdiction | null): string {
  return resolveJurisdiction(jurisdiction) === 'SG'
    ? TUTORIAL_PATHS.gettingStarted.SG
    : TUTORIAL_PATHS.gettingStarted.MY;
}

/** 통합 사용자 가이드 — 국가별 해시(#/sg | #/my). */
export function getUserGuideUrl(jurisdiction?: Jurisdiction | null): string {
  const hash = resolveJurisdiction(jurisdiction) === 'SG' ? '#/sg' : '#/my';
  return `${TUTORIAL_PATHS.userGuide}${hash}`;
}

/** T-Link — SG/MY 공통 */
export function getTLinkWalkthroughUrl(): string {
  return TUTORIAL_PATHS.tLink;
}

/** Authorisation Settings — SG/MY 공통 */
export function getAuthorisationSettingsWalkthroughUrl(): string {
  return TUTORIAL_PATHS.authorisationSettings;
}

/** Start Task (T-Doc AI) — SG/MY 공통 */
export function getTDocAiWalkthroughUrl(): string {
  return TUTORIAL_PATHS.tDocAi;
}

/** T-Doc AI Variable Setup Guide — SG/MY 공통 */
export function getTDocVariablesGuideUrl(): string {
  return TUTORIAL_PATHS.tDocVariablesGuide;
}

/** T-Sign — SG/MY 공통 */
export function getTSignWalkthroughUrl(): string {
  return TUTORIAL_PATHS.tSign;
}

/** T-Doc — SG/MY 공통 */
export function getTDocWalkthroughUrl(): string {
  return TUTORIAL_PATHS.tDoc;
}

/** Annual Return (AR) — MY 전용 */
export function getAnnualReturnWalkthroughUrl(): string {
  return TUTORIAL_PATHS.annualReturn;
}

/** Reminder — SG/MY 공통 */
export function getReminderWalkthroughUrl(): string {
  return TUTORIAL_PATHS.reminder;
}

/** User Invitation — SG/MY 공통 */
export function getUserInviteWalkthroughUrl(): string {
  return TUTORIAL_PATHS.userInvite;
}

/** Help 메뉴 Tutorials 드릴인에 노출할 한 항목. */
export interface TutorialItem {
  key: string;
  title: string;
  subtitle: string;
  /** MDI 아이콘 이름 (예: 'mdi-shield-account-outline') */
  icon: string;
  url: string;
}

/**
 * Help 메뉴의 Tutorials 목록 — 사용중인 국가 버전에 맞게 구성.
 * (User Invitation 은 Help 목록에 포함하지 않음)
 */
export function getHelpTutorials(jurisdiction?: Jurisdiction | null): TutorialItem[] {
  const j = resolveJurisdiction(jurisdiction);
  const isSg = j === 'SG';

  const items: TutorialItem[] = [
    {
      key: 'authorisation-settings',
      title: 'Authorisation Settings',
      subtitle: 'Departments · permissions',
      icon: 'mdi-shield-account-outline',
      url: TUTORIAL_PATHS.authorisationSettings,
    },
    {
      key: 'start-task-preset',
      title: 'Start Task (Preset)',
      subtitle: 'Preset → documents',
      icon: 'mdi-format-list-checks',
      url: getStartTaskWalkthroughUrl(j),
    },
    {
      key: 'start-task-tdoc-ai',
      title: 'Start Task (T-Doc AI)',
      subtitle: 'Upload & AI document generation',
      icon: 'mdi-creation',
      url: TUTORIAL_PATHS.tDocAi,
    },
    {
      key: 'e-filing',
      title: 'e-Filing',
      subtitle: isSg ? 'Lodge with ACRA · BizFile' : 'Lodge with SSM',
      icon: 'mdi-tray-arrow-down',
      url: getEFilingWalkthroughUrl(j),
    },
    {
      key: 't-sign',
      title: 'T-Sign',
      subtitle: 'e-signatures',
      icon: 'mdi-draw-pen',
      url: TUTORIAL_PATHS.tSign,
    },
    {
      key: 't-link',
      title: 'T-Link',
      subtitle: 'Public Deep Link intake',
      icon: 'mdi-link-variant',
      url: TUTORIAL_PATHS.tLink,
    },
  ];

  // Annual Return (AR) — MY 전용, e-Filing 흐름과 가까운 위치에 노출
  if (!isSg) {
    items.push({
      key: 'annual-return',
      title: 'Annual Return (AR)',
      subtitle: 'MBRS/XBRL · SSM',
      icon: 'mdi-calendar-check-outline',
      url: TUTORIAL_PATHS.annualReturn,
    });
  }

  items.push({
    key: 'reminder',
    title: 'Reminder Management',
    subtitle: 'Email reminders · levels',
    icon: 'mdi-bell-outline',
    url: TUTORIAL_PATHS.reminder,
  });

  items.push({
    key: 't-doc',
    title: 'T-Doc',
    subtitle: 'Template manager · variables',
    icon: 'mdi-file-document-outline',
    url: TUTORIAL_PATHS.tDoc,
  });

  items.push({
    key: 't-doc-variables',
    title: 'T-Doc AI Variable Setup Guide',
    subtitle: 'Make a document in minutes (EN/KR)',
    icon: 'mdi-file-check-outline',
    url: TUTORIAL_PATHS.tDocVariablesGuide,
  });

  return items;
}
